import { ApiClientError, apiRequest, createIdempotencyKey, getFanToArtistMessageHistory } from "@kira/api-client";
import type {
  Artist,
  ArtistMessage,
  ArtistQueryInput,
  CartItem,
  Circle,
  CirclePreference,
  CirclePreferencesInput,
  CircleQueryInput,
  Conversation,
  CreateCircleInput,
  CreatePostInput,
  CreateProductInput,
  FanToArtistMessage,
  FanToArtistMessagePage,
  FanRegistrationInput,
  NotificationReadResult,
  Order,
  Post,
  PostInteractionResult,
  CampaignParticipationResult,
  Product,
  ShipOrderInput,
  SubscriptionOrder,
  SubscriptionPlan,
  SystemNotification,
  User,
  UpdateProfileInput,
} from "@kira/contracts";

const CACHE_PREFIX = "kira:fan-cache:";
export const FAN_SESSION_CACHE_KEY = `${CACHE_PREFIX}session-user`;
const LOGIN_ATTEMPT_KEY = `${CACHE_PREFIX}login-attempt`;

export class SessionChangedError extends Error {
  constructor() {
    super("登录身份已在另一窗口更新，请重新进入");
    this.name = "SessionChangedError";
  }
}

export function clearFanDataCaches(): void {
  for (let index = localStorage.length - 1; index >= 0; index -= 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(CACHE_PREFIX) && key !== FAN_SESSION_CACHE_KEY && key !== LOGIN_ATTEMPT_KEY) localStorage.removeItem(key);
  }
}

function clearForeignFanDataCaches(activeUserId: string): void {
  const activePrefix = `${CACHE_PREFIX}${activeUserId}:`;
  for (let index = localStorage.length - 1; index >= 0; index -= 1) {
    const key = localStorage.key(index);
    if (key?.startsWith(CACHE_PREFIX) && key !== FAN_SESSION_CACHE_KEY && key !== LOGIN_ATTEMPT_KEY && !key.startsWith(activePrefix)) {
      localStorage.removeItem(key);
    }
  }
}

function cacheUser(user: User): User {
  const previous = getCachedMe();
  if (!previous || previous.id !== user.id) clearFanDataCaches();
  localStorage.setItem(FAN_SESSION_CACHE_KEY, JSON.stringify(user));
  // A request from the page being replaced can settle just after an account
  // switch. Re-check shortly after navigation so that late writes from the
  // previous identity cannot survive on a shared device.
  for (const delay of [0, 250, 1_000]) {
    window.setTimeout(() => {
      if (getCachedMe()?.id === user.id) clearForeignFanDataCaches(user.id);
    }, delay);
  }
  return user;
}

export function getCachedMe(): User | null {
  try {
    const cached = localStorage.getItem(FAN_SESSION_CACHE_KEY);
    return cached ? JSON.parse(cached) as User : null;
  } catch {
    localStorage.removeItem(FAN_SESSION_CACHE_KEY);
    return null;
  }
}

function listFrom<T>(value: unknown, key: string): T[] {
  if (Array.isArray(value)) return value as T[];
  if (value && typeof value === "object") {
    const candidate = (value as Record<string, unknown>)[key];
    if (Array.isArray(candidate)) return candidate as T[];
  }
  return [];
}

async function cachedGet<T>(path: string, cacheKey: string): Promise<T> {
  const userId = getCachedMe()?.id;
  const scopedCacheKey = userId ? `${CACHE_PREFIX}${userId}:${cacheKey}` : null;
  try {
    const data = await apiRequest<T>(path);
    if (scopedCacheKey && getCachedMe()?.id === userId) {
      localStorage.setItem(scopedCacheKey, JSON.stringify({ savedAt: Date.now(), data }));
    }
    return data;
  } catch (error) {
    const mayUseOfflineCache = error instanceof ApiClientError
      && error.status === 0
      && (error.code === "NETWORK_ERROR" || error.code === "REQUEST_TIMEOUT");
    if (!mayUseOfflineCache || !scopedCacheKey || getCachedMe()?.id !== userId) throw error;
    const cached = localStorage.getItem(scopedCacheKey);
    if (!cached) throw error;
    try {
      const parsed = JSON.parse(cached) as { data: T };
      window.dispatchEvent(new CustomEvent("kira:cache-fallback", { detail: { cacheKey: scopedCacheKey } }));
      return parsed.data;
    } catch {
      localStorage.removeItem(scopedCacheKey);
      throw error;
    }
  }
}

export interface RankingGroups {
  month: Artist[];
  week: Artist[];
  day: Artist[];
}

export interface FanHome {
  artists: Artist[];
  featuredArtists: Artist[];
  rankings: RankingGroups;
  circles: Circle[];
  newArtists: Artist[];
  subscriptions: Artist[];
  encounters: FanEncounter[];
  posts: Post[];
  unreadCount: number;
}

export interface FanEncounter {
  artist: Artist;
  reason: string;
  score: number;
}

export interface FanStats {
  following: number;
  followers: number;
  subscriptions: number;
  fandomDays: number;
  level: number;
  points: number;
}

export interface FanProfile {
  followedCircles: Circle[];
  user: User;
  stats: FanStats;
  badges: string[];
  posts: Post[];
  likedPosts: Post[];
  savedPosts: Post[];
}

export interface PushConfig {
  publicKey?: string;
  vapidPublicKey?: string;
  enabled?: boolean;
}

export async function demoLogin(userId = "fan_demo"): Promise<User | null> {
  const attemptId = createIdempotencyKey("fan-login");
  localStorage.setItem(LOGIN_ATTEMPT_KEY, attemptId);
  const raw = await apiRequest<User | { user: User }>("/api/auth/demo", { method: "POST", body: { userId } });
  if (localStorage.getItem(LOGIN_ATTEMPT_KEY) !== attemptId) {
    await getMe().catch(() => undefined);
    throw new SessionChangedError();
  }
  localStorage.removeItem(LOGIN_ATTEMPT_KEY);
  return cacheUser("user" in raw ? raw.user : raw);
}

export async function registerFan(input: FanRegistrationInput): Promise<User> {
  const attemptId = createIdempotencyKey("fan-registration");
  localStorage.setItem(LOGIN_ATTEMPT_KEY, attemptId);
  try {
    const raw = await apiRequest<User | { user: User }>("/api/auth/fan/register", {
      method: "POST",
      body: input,
    });
    if (localStorage.getItem(LOGIN_ATTEMPT_KEY) !== attemptId) {
      await getMe().catch(() => undefined);
      throw new SessionChangedError();
    }
    return cacheUser("user" in raw ? raw.user : raw);
  } finally {
    if (localStorage.getItem(LOGIN_ATTEMPT_KEY) === attemptId) {
      localStorage.removeItem(LOGIN_ATTEMPT_KEY);
    }
  }
}

export async function getMe(): Promise<User> {
  const sessionSnapshot = localStorage.getItem(FAN_SESSION_CACHE_KEY);
  const raw = await apiRequest<User | { user: User }>("/api/auth/me");
  const resolved = "user" in raw ? raw.user : raw;
  const currentSnapshot = localStorage.getItem(FAN_SESSION_CACHE_KEY);
  if (currentSnapshot !== sessionSnapshot) {
    const current = getCachedMe();
    if (!current || current.id !== resolved.id) throw new SessionChangedError();
    return current;
  }
  return cacheUser(resolved);
}

export async function logout(): Promise<void> {
  localStorage.removeItem(LOGIN_ATTEMPT_KEY);
  localStorage.removeItem(FAN_SESSION_CACHE_KEY);
  clearFanDataCaches();
  await apiRequest<void>("/api/auth/logout", { method: "POST" });
}

export async function getFanHome(): Promise<FanHome> {
  const raw = await cachedGet<unknown>("/api/fan/home", "home");
  const record = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const artists = listFrom<Artist>(record.artists ?? record.featuredArtists ?? record, "artists");
  const featuredArtists = listFrom<Artist>(record.featuredArtists ?? record.newArtists, "featuredArtists");
  const rankingRaw = record.rankings;
  let month: Artist[] = [];
  let week: Artist[] = [];
  let day: Artist[] = [];
  if (Array.isArray(rankingRaw)) month = rankingRaw as Artist[];
  if (rankingRaw && typeof rankingRaw === "object") {
    const rankings = rankingRaw as Record<string, unknown>;
    const unwrap = (value: unknown) => listFrom<Artist | { artist: Artist }>(value, "items").map((entry) => "artist" in entry ? entry.artist : entry);
    month = unwrap(rankings.month ?? rankings.monthly);
    week = unwrap(rankings.week ?? rankings.weekly);
    day = unwrap(rankings.day ?? rankings.daily);
  }
  return {
    artists,
    featuredArtists: featuredArtists.length ? featuredArtists : artists.slice(0, 4),
    rankings: { month: month.length ? month : artists, week: week.length ? week : month, day: day.length ? day : week },
    circles: listFrom<Circle>(record.circles, "circles"),
    newArtists: listFrom<Artist>(record.newArtists, "newArtists"),
    subscriptions: listFrom<Artist>(record.subscriptions, "subscriptions"),
    encounters: listFrom<FanEncounter>(record.encounters, "encounters"),
    posts: listFrom<Post>(record.posts, "posts"),
    unreadCount: typeof record.unreadCount === "number" ? record.unreadCount : 0,
  };
}

export async function getEncounters(): Promise<FanEncounter[]> {
  const raw = await apiRequest<unknown>("/api/fan/encounters");
  return listFrom<FanEncounter>(raw, "encounters");
}

export async function getArtists(query: Partial<ArtistQueryInput> = {}): Promise<Artist[]> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) if (value !== undefined) params.set(key, String(value));
  const raw = await apiRequest<unknown>(`/api/artists${params.size ? `?${params}` : ""}`);
  return listFrom<Artist>(raw, "artists");
}

export const mutateSubscription = (artistId: string, action: "unsubscribe" | "block" | "unblock") =>
  apiRequest<Artist | { artist: Artist }>("/api/fan/subscriptions", {
    method: "POST",
    body: { artistId, action },
    idempotencyKey: createIdempotencyKey(`subscription:${artistId}:${action}`),
  }).then((result) => { clearFanDataCaches(); return "artist" in result ? result.artist : result; });

export async function getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
  const raw = await apiRequest<unknown>("/api/fan/subscription-plans");
  return listFrom<SubscriptionPlan>(raw, "plans");
}

export async function getSubscriptionOrders(): Promise<SubscriptionOrder[]> {
  const raw = await apiRequest<unknown>("/api/fan/subscription-orders");
  return listFrom<SubscriptionOrder>(raw, "orders");
}

export const createSubscriptionOrder = (artistIds: string[], operationKey = createIdempotencyKey("subscription-order")) =>
  apiRequest<SubscriptionOrder>("/api/fan/subscription-orders", {
    method: "POST",
    body: { artistIds },
    idempotencyKey: operationKey,
  });

export async function getConversations(): Promise<Conversation[]> {
  const raw = await cachedGet<unknown>("/api/fan/conversations", "conversations");
  return listFrom<Conversation>(raw, "conversations");
}

export async function getMessages(artistId: string, after?: number): Promise<ArtistMessage[]> {
  const query = new URLSearchParams({ artistId });
  if (after !== undefined) query.set("after", String(after));
  const raw = after === undefined
    ? await cachedGet<unknown>(`/api/fan/messages?${query}`, `messages:${artistId}`)
    : await apiRequest<unknown>(`/api/fan/messages?${query}`);
  const messages = listFrom<ArtistMessage>(raw, "messages");
  return messages.length ? messages : listFrom<ArtistMessage>(raw, "items");
}

export const readMessages = (messageIds: string[]) =>
  apiRequest<void>("/api/fan/messages/read", { method: "POST", body: { messageIds } });

export const leaveArtistMessage = (artistId: string, content: string, operationKey = createIdempotencyKey(`artist-note:${artistId}`)) =>
  apiRequest<FanToArtistMessage>("/api/fan/artist-messages", {
    method: "POST",
    body: { artistId, content },
    idempotencyKey: operationKey,
  });

export const getFanMessageHistory = (
  artistId: string,
  options: { before?: string; limit?: number } = {},
): Promise<FanToArtistMessagePage> => getFanToArtistMessageHistory(artistId, options);

export async function getNotifications(): Promise<SystemNotification[]> {
  const raw = await apiRequest<unknown>("/api/notifications");
  return listFrom<SystemNotification>(raw, "notifications");
}

export const readNotifications = (notificationIds: string[]) =>
  apiRequest<NotificationReadResult>("/api/notifications/read", {
    method: "POST",
    body: { notificationIds },
  });

export type { SystemNotification };

export async function getCircles(query: Partial<CircleQueryInput> = {}): Promise<Circle[]> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) if (value !== undefined) params.set(key, String(value));
  const suffix = params.size ? `?${params}` : "";
  const raw = await cachedGet<unknown>(`/api/circles${suffix}`, `circles:${params.toString()}`);
  return listFrom<Circle>(raw, "circles");
}

export async function getCirclePreferences(): Promise<CirclePreference[]> {
  const raw = await apiRequest<unknown>("/api/fan/circle-preferences");
  return listFrom<CirclePreference>(raw, "items");
}

export const setCirclePreferences = (input: CirclePreferencesInput) =>
  apiRequest<CirclePreference[]>("/api/fan/circle-preferences", { method: "PUT", body: input, idempotencyKey: createIdempotencyKey("circle-preferences") });

export async function getPosts(circleId?: string, sort: "latest" | "active" | "featured" = "latest"): Promise<Post[]> {
  const query = new URLSearchParams({ sort: sort === "active" ? "hot" : sort });
  if (circleId) query.set("circleId", circleId);
  const raw = await cachedGet<unknown>(`/api/posts?${query}`, `posts:${circleId ?? "all"}:${sort}`);
  return listFrom<Post>(raw, "posts");
}

export const newOperationKey = (scope: string) => createIdempotencyKey(scope);

export const createPost = (input: CreatePostInput, operationKey = createIdempotencyKey("post")) =>
  apiRequest<Post>("/api/posts", { method: "POST", body: input, idempotencyKey: operationKey });

export const mutatePostInteraction = (postId: string, action: "like" | "unlike" | "save" | "unsave") =>
  apiRequest<PostInteractionResult>(`/api/posts/${encodeURIComponent(postId)}/interaction`, { method: "POST", body: { action } });

export const publishCampaignDraft = (postId: string, operationKey = createIdempotencyKey(`campaign-publish:${postId}`)) =>
  apiRequest<Post>(`/api/campaigns/${encodeURIComponent(postId)}/publish`, { method: "POST", body: {}, idempotencyKey: operationKey });

export const mutateCampaignParticipation = (postId: string, action: "join" | "leave") =>
  apiRequest<CampaignParticipationResult>(`/api/campaigns/${encodeURIComponent(postId)}/participation`, { method: "POST", body: { action } });

export const updateFanProfile = (input: UpdateProfileInput, operationKey = createIdempotencyKey("profile-update")) =>
  apiRequest<User>("/api/fan/profile", { method: "PATCH", body: input, idempotencyKey: operationKey });

export function cacheCurrentUser(user: User, expectedUserId: string): User {
  if (user.id !== expectedUserId || getCachedMe()?.id !== expectedUserId) throw new SessionChangedError();
  return cacheUser(user);
}

export const createCircle = (input: CreateCircleInput, operationKey = createIdempotencyKey("circle")) =>
  apiRequest<Circle>("/api/circles", { method: "POST", body: input, idempotencyKey: operationKey });

export const mutateCircleMembership = (circleId: string, action: "follow" | "unfollow") =>
  apiRequest<Circle>(`/api/circles/${encodeURIComponent(circleId)}/membership`, {
    method: "POST",
    body: { action },
  });

export async function getProducts(): Promise<Product[]> {
  const raw = await cachedGet<unknown>("/api/market/products", "market:products");
  return listFrom<Product>(raw, "products");
}

export async function getCart(): Promise<CartItem[]> {
  const raw = await cachedGet<unknown>("/api/market/cart", "market:cart");
  return listFrom<CartItem>(raw, "cart");
}

export async function getOrders(): Promise<Order[]> {
  const raw = await cachedGet<unknown>("/api/market/orders", "market:orders");
  return listFrom<Order>(raw, "orders");
}

export async function getSellerOrders(): Promise<Order[]> {
  const raw = await apiRequest<unknown>("/api/market/seller/orders");
  return listFrom<Order>(raw, "orders");
}

export async function updateCart(productId: string, quantity: number, selected?: boolean): Promise<CartItem[]> {
  const body = selected === undefined ? { productId, quantity } : { productId, quantity, selected };
  const raw = await apiRequest<unknown>("/api/market/cart", { method: "PUT", body });
  return listFrom<CartItem>(raw, "cart");
}

export const createProduct = (input: CreateProductInput, operationKey = createIdempotencyKey(input.isCarpool ? "carpool" : "product")) =>
  apiRequest<Product>("/api/market/products", {
    method: "POST",
    body: input,
    idempotencyKey: operationKey,
  });

export const shipSellerOrder = (orderId: string, input: ShipOrderInput, operationKey = createIdempotencyKey(`shipment:${orderId}`)) =>
  apiRequest<Order>(`/api/market/seller/orders/${encodeURIComponent(orderId)}/ship`, {
    method: "POST",
    body: input,
    idempotencyKey: operationKey,
  });

export const checkout = async (productIds: string[], operationKey = createIdempotencyKey("checkout")): Promise<Order[]> => {
  const raw = await apiRequest<Order | { orders: Order[] }>("/api/market/checkout", {
    method: "POST",
    body: { productIds },
    idempotencyKey: operationKey,
  });
  return "orders" in raw ? raw.orders : [raw];
};

export async function getProfile(): Promise<FanProfile> {
  const raw = await cachedGet<Record<string, unknown>>("/api/profile", "profile");
  const source = (raw.user ?? raw.profile ?? raw) as User;
  const statsRaw = (raw.stats ?? {}) as Partial<FanStats>;
  const subscriptions = listFrom<Artist>(raw.subscriptions, "subscriptions");
  const followedCircles = listFrom<Circle>(raw.followedCircles, "followedCircles");
  const fandomDays = source.createdAt
    ? Math.max(1, Math.floor((Date.now() - new Date(source.createdAt).getTime()) / 86_400_000))
    : 1;
  return {
    user: source,
    followedCircles,
    stats: {
      following: statsRaw.following ?? followedCircles.length,
      followers: statsRaw.followers ?? 0,
      subscriptions: statsRaw.subscriptions ?? subscriptions.length,
      fandomDays: statsRaw.fandomDays ?? fandomDays,
      level: statsRaw.level ?? Math.max(1, Math.min(99, 1 + Math.floor(fandomDays / 30))),
      points: statsRaw.points ?? subscriptions.reduce((sum, artist) => sum + Math.max(1, Math.round(artist.heatDelta / 100)), 0),
    },
    badges: listFrom<string>(raw.badges, "badges").length
      ? listFrom<string>(raw.badges, "badges")
      : [source.phoneVerified ? "手机已认证" : "初次入场", ...(source.realnameVerified ? ["实名守护者"] : [])],
    posts: listFrom<Post>(raw.posts, "posts"),
    likedPosts: listFrom<Post>(raw.likedPosts, "likedPosts"),
    savedPosts: listFrom<Post>(raw.savedPosts, "savedPosts"),
  };
}

export const getPushConfig = () => apiRequest<PushConfig>("/api/push/config");

export const savePushSubscription = (subscription: PushSubscriptionJSON) =>
  apiRequest<void>("/api/push/subscriptions", { method: "POST", body: subscription });

export const removePushSubscription = (endpoint: string) =>
  apiRequest<void>("/api/push/subscriptions", { method: "DELETE", body: { endpoint } });
